import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react'
import { Link, NavLink, useLocation } from 'react-router'
import { STATUS_LINKS } from '@/status/products'
import { MotionToggle } from './MotionToggle'
import { ShareButton, siteShareContext } from './ShareSheet'
import { SoundToggle } from './SoundToggle'
import { WALLS_LISTED } from './wallsListed'

/*
 * The bar's two halves, with the Status menu between them.
 *
 * Status sits second because it is the reason most visitors arrive: the home page is the joke and
 * `/status/*` is the part that answers the question they typed. It is not a plain link because
 * there are seven of those pages now (three providers and four products, F31) and a menu is the
 * only way the bar can offer all of them without becoming seven items wide.
 */
const NAV_BEFORE = [
  { to: '/', label: 'Live', end: true },
  { to: '/v2', label: 'Walls', end: true },
].filter((item) => item.to !== '/v2' || WALLS_LISTED)

const NAV_AFTER = [
  { to: '/history', label: 'History', end: false },
  { to: '/codex', label: 'Resets', end: false },
  { to: '/about', label: 'About', end: false },
]

/**
 * Where the bar has room to hang a menu under it.
 *
 * Below this the row is already scrolling sideways to fit five links (`chrome.css`), and a popup
 * anchored inside a scroll container is a popup that scrolls away from its own trigger. So the
 * narrow bar links straight to `/status/openai` and the footer carries the full list — which is
 * also the arrangement `layout.spec.ts` measures at 320–673 px, where nothing may widen the page.
 */
const WIDE_BAR = '(min-width: 60rem)'

/**
 * The top bar: the wordmark, the places to go, and the two controls that apply everywhere.
 *
 * It is deliberately a *bar* and not a hero. The site's headline — what is down, right now — is a
 * sentence that changes with the world, and it belongs to the page that can say it (`Hero.tsx` on
 * `/` and `/v2`), not to chrome that also tops the archive and the About page. Keeping this row to
 * one line of quiet controls is what lets the status underneath be the first thing anybody reads.
 *
 * The wordmark is a link home rather than a heading on every route: a `<p>` here beside a page's
 * own `<h1>` leaves a screen-reader user with two top-level headings and no way to tell which one
 * describes the content. The home page's `<h1>` is its status sentence.
 */
export function Header() {
  return (
    <header className="mha-topbar">
      <Link to="/" className="mha-topbar__brand" aria-label="Must Have AI — home">
        <span aria-hidden="true" className="mha-topbar__mark" />
        <span className="mha-display mha-topbar__name">Must Have AI</span>
      </Link>

      <nav aria-label="Primary" className="mha-topbar__nav">
        <ul className="mha-nav">
          {NAV_BEFORE.map((item) => (
            <li key={item.to}>
              <NavLink to={item.to} end={item.end} className="mha-nav__link">
                {item.label}
              </NavLink>
            </li>
          ))}

          <StatusMenu />

          {NAV_AFTER.map((item) => (
            <li key={item.to}>
              <NavLink to={item.to} end={item.end} className="mha-nav__link">
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <div className="mha-topbar__tools">
        {/*
          Beside the nav rather than inside it: sharing is not a place to go, and a visitor
          scanning four page links must not have to read past a fifth item that is not one.
          `siteShareContext` is passed as a function so this header - which renders on every
          route - never subscribes to a store that changes twice a second.
        */}
        <ShareButton
          context={siteShareContext}
          describe="open the share sheet for the current state of the site"
        />
        <SoundToggle />
        {/* Renders nothing unless this visitor's system asked for reduced motion. */}
        <MotionToggle />
      </div>
    </header>
  )
}

/**
 * The Status entry: a disclosure listing all seven status pages, or one link to `/status/openai`.
 *
 * A disclosure and not an ARIA menu. These are seven ordinary links to seven ordinary pages, and
 * `role="menu"` would promise a screen-reader user an application menu — no browser back, no
 * open-in-new-tab, arrow keys only. The button says whether the list is open and the list is a
 * `<ul>` of `<a>`, which is what they are.
 */
function StatusMenu() {
  const wide = useWideBar()
  const listId = useId()
  const entry = useRef<HTMLLIElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const list = useRef<HTMLUListElement>(null)
  const { pathname } = useLocation()

  /*
   * Open *on a route*, rather than a boolean plus an effect that closes it on every navigation.
   *
   * A menu still hanging over the page it just left is chrome that has stopped describing the
   * site, and following one of its own links is the commonest way out of it. Storing the route it
   * was opened on makes "closed after a navigation" something this renders rather than something
   * it has to remember to do — including for the back button, which no click handler would catch.
   */
  const [openAt, setOpenAt] = useState<string | null>(null)
  const open = openAt === pathname

  const setOpen = useCallback(
    (next: boolean) => setOpenAt(next ? pathname : null),
    [pathname],
  )

  const close = useCallback(
    (focusTrigger: boolean) => {
      setOpenAt(null)
      if (focusTrigger) trigger.current?.focus()
    },
    [],
  )

  /** Focus the nth link, counting from the end for a negative index, and wrapping at both ends. */
  const focusItem = useCallback((index: number) => {
    const items = list.current?.querySelectorAll<HTMLAnchorElement>('a')
    if (items === undefined || items.length === 0) return
    items[(index + items.length) % items.length]?.focus()
  }, [])

  /*
   * An arrow on the trigger opens the list *and* enters it, which is what makes the keyboard path
   * as short as the pointer one. The list is not in the DOM until the opening render commits, so
   * the index is parked here and spent by the effect below rather than focused in the handler.
   */
  const entering = useRef<number | null>(null)
  useEffect(() => {
    if (!open || entering.current === null) return
    const index = entering.current
    entering.current = null
    focusItem(index)
  }, [open, focusItem])

  // A press anywhere else closes it. `pointerdown` rather than `click` so the menu is gone before
  // whatever was pressed reacts, and the whole entry is the "inside" region — the trigger included,
  // or pressing it while open would close and immediately reopen.
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent): void => {
      const inside =
        event.target instanceof Node && entry.current !== null && entry.current.contains(event.target)
      if (!inside) setOpenAt(null)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  if (!wide) {
    return (
      <li>
        {/* One tap to the biggest of the seven; the other six are in the footer, in full. */}
        <NavLink to="/status/openai" className="mha-nav__link">
          Status
        </NavLink>
      </li>
    )
  }

  function onTriggerKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      entering.current = event.key === 'ArrowUp' ? -1 : 0
      setOpen(true)
    } else if (event.key === 'Escape') {
      close(false)
    }
  }

  function onListKeyDown(event: ReactKeyboardEvent<HTMLUListElement>): void {
    const items = [...(list.current?.querySelectorAll<HTMLAnchorElement>('a') ?? [])]
    const at = items.indexOf(document.activeElement as HTMLAnchorElement)

    if (event.key === 'Escape') {
      event.preventDefault()
      close(true)
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      focusItem(at + 1)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      focusItem(at - 1)
    } else if (event.key === 'Home') {
      event.preventDefault()
      focusItem(0)
    } else if (event.key === 'End') {
      event.preventDefault()
      focusItem(items.length - 1)
    } else if (event.key === 'Tab') {
      // Tab out is a way out. Left open, the list would sit over the page while focus is three
      // controls away, and the next Escape would belong to something else.
      setOpen(false)
    }
  }

  return (
    <li className="mha-nav__entry" ref={entry}>
      <button
        type="button"
        className="mha-nav__link mha-nav__trigger"
        aria-expanded={open}
        aria-controls={listId}
        ref={trigger}
        onClick={() => setOpen(!open)}
        onKeyDown={onTriggerKeyDown}
      >
        Status
        <span className="mha-nav__caret" aria-hidden="true" />
      </button>

      {open ? (
        <ul className="mha-statusmenu" id={listId} ref={list} onKeyDown={onListKeyDown}>
          {STATUS_LINKS.map((link) => (
            <li key={link.to}>
              <NavLink className="mha-statusmenu__link" to={link.to}>
                {link.label}
              </NavLink>
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  )
}

/**
 * Is the bar wide enough for the menu?
 *
 * Read at first render rather than after an effect, so a phone never paints the desktop trigger for
 * a frame and then replaces it — a control that changes shape after the page settles is a control
 * somebody is already pressing. A browser without `matchMedia` (and jsdom, which has none unless a
 * test installs one) is treated as wide: a list nobody can open is a worse failure than a menu on a
 * narrow screen.
 */
function useWideBar(): boolean {
  const [wide, setWide] = useState(readWideBar)

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const media = window.matchMedia(WIDE_BAR)
    const update = (): void => setWide(media.matches)
    update()
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [])

  return wide
}

function readWideBar(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true
  try {
    return window.matchMedia(WIDE_BAR).matches
  } catch {
    return true
  }
}
